import { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useMintRelease, useUploadArtefact } from '../../api/queries';

export type NewReleaseDialogProps = {
  open: boolean;
  onClose: () => void;
};

const TARGETS = [
  { value: 'xu4',          label: 'XU4 (Exynos 5422, armhf — site-controller OS image)' },
  { value: 'c4',           label: 'C4 (Amlogic S905X3, aarch64 — site-controller OS image)' },
  { value: 'm1s',          label: 'M1S (Rockchip RK3566, aarch64 — onboard eMMC site-controller OS image)' },
  { value: 'cm5-os',       label: 'CM5-OS (Raspberry Pi production — A/B site-controller OS image)' },
  { value: 'cm4-os',       label: 'CM4-OS (Raspberry Pi Waveshare dual-ETH — A/B site-controller OS image)' },
  { value: 'odroid-c2',    label: 'Odroid C2 (legacy — site-controller OS image)' },
  { value: 'sc-app',       label: 'sc-app (on-device .NET worker — legacy XU4)' },
  { value: 'sc-runtime',   label: 'sc-runtime (RM-distributed SC runtime — Stage 3+)' },
  { value: 'sc-bootloader',label: 'sc-bootloader (u-boot signed image)' },
  { value: 'mg-firmware',  label: 'MachineGateway container' },
  { value: 'esp32p4',      label: 'ESP32-P4 firmware' },
  { value: 'mac-tool',     label: 'Mac site-preparation tool' },
];

const ROLES = [
  { value: 'sc-app',          label: 'sc-app — on-device worker (.zip, legacy)' },
  { value: 'sc-runtime',      label: 'sc-runtime — RM-distributed SC runtime (.zip, Stage 3+)' },
  { value: 'sc-os',           label: 'sc-os — full OS image (.img / .img.xz)' },
  { value: 'sc-bootloader',   label: 'sc-bootloader — u-boot signed image' },
  { value: 'mg-firmware',     label: 'mg-firmware — container tarball' },
  { value: 'esp32-firmware',  label: 'esp32-firmware — signed binary' },
  { value: 'mac-tool',        label: 'mac-tool — .dmg' },
];

/**
 * R5 — minimal "publish a new release" surface for ACES.
 *
 * Two-step flow that mirrors the publish-release.sh script:
 *
 *   1. POST /api/v1/releases           — mints a draft release row
 *   2. POST .../artefacts/{role}       — uploads the raw bytes
 *
 * A single click here does both, which is what 90% of releases need
 * (one role per release). Multi-artefact releases can attach extras
 * by re-opening the catalogue row's detail view (future iteration).
 */
export function NewReleaseDialog({ open, onClose }: NewReleaseDialogProps) {
  const [target, setTarget]           = useState('sc-app');
  const [role, setRole]               = useState('sc-app');
  const [version, setVersion]         = useState('');
  const [displayName, setDisplayName] = useState('');
  const [channel, setChannel]         = useState('stable');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [file, setFile]               = useState<File | null>(null);
  const [error, setError]             = useState<string | null>(null);
  const [okMsg, setOkMsg]             = useState<string | null>(null);

  const mintMut   = useMintRelease();
  const uploadMut = useUploadArtefact();

  const reset = () => {
    setTarget('sc-app'); setRole('sc-app'); setVersion(''); setDisplayName('');
    setChannel('stable'); setReleaseNotes(''); setFile(null);
    setError(null); setOkMsg(null);
  };

  const submit = async () => {
    setError(null); setOkMsg(null);
    if (!version.trim()) { setError('Version is required (e.g. 1.4.2).'); return; }
    if (!file)           { setError('Pick the artefact file to upload.'); return; }
    try {
      const minted = await mintMut.mutateAsync({
        target, version: version.trim(),
        displayName: displayName.trim() || undefined,
        channel: channel || undefined,
        releaseNotesMd: releaseNotes || undefined,
      });
      const uploaded = await uploadMut.mutateAsync({
        releaseId: minted.id, role, file,
      });
      setOkMsg(
        `Release ${minted.target}@${minted.version} created · ` +
        `artefact ${uploaded.role} uploaded · sha256=${uploaded.sha256.slice(0, 12)}… · ` +
        `${(uploaded.size_bytes / 1024).toFixed(1)} KiB. ` +
        `State=draft — promote it to in_test → released from the catalogue row.`,
      );
      // Reset form but keep dialog open so the user reads the success msg.
      setVersion(''); setDisplayName(''); setReleaseNotes(''); setFile(null);
    } catch (e) {
      const m = (e as { message?: string })?.message ?? 'Upload failed.';
      setError(m);
    }
  };

  const busy = mintMut.isPending || uploadMut.isPending;

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="md" fullWidth>
      <DialogTitle>Publish new release</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {okMsg && <Alert severity="success" onClose={() => setOkMsg(null)}>{okMsg}</Alert>}

          <Stack direction="row" spacing={2}>
            <TextField select fullWidth label="Target" value={target} onChange={e => setTarget(e.target.value)}>
              {TARGETS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Artefact role" value={role} onChange={e => setRole(e.target.value)}>
              {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="Version" placeholder="1.4.2" value={version} onChange={e => setVersion(e.target.value)} required />
            <TextField fullWidth label="Display name (optional)" placeholder="arms-sc-app-1.4.2-linux-arm" value={displayName} onChange={e => setDisplayName(e.target.value)} />
            <TextField select label="Channel" value={channel} onChange={e => setChannel(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="stable">stable</MenuItem>
              <MenuItem value="beta">beta</MenuItem>
              <MenuItem value="dev">dev</MenuItem>
            </TextField>
          </Stack>

          <TextField
            multiline minRows={2} maxRows={6} fullWidth
            label="Release notes (optional, markdown)"
            value={releaseNotes} onChange={e => setReleaseNotes(e.target.value)}
          />

          <Box>
            <Button variant="outlined" component="label" disabled={busy}>
              {file ? `Selected: ${file.name}` : 'Pick artefact file'}
              <input
                hidden type="file"
                onChange={e => setFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            {file && (
              <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                {(file.size / 1024 / 1024).toFixed(2)} MiB · {file.type || 'application/octet-stream'}
              </Typography>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Uploaded as a Postgres bytea blob; SHA-256 is computed server-side. The release
            starts in <strong>draft</strong> state — promote it from the catalogue row once the
            artefact has been smoke-tested.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }} disabled={busy}>Close</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? 'Uploading…' : 'Create draft + upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
